import { useMemo, useState, useCallback } from "react";
import {
  ActivityIndicator,
  View,
  StyleSheet,
  RefreshControl,
  TouchableOpacity,
  SectionList,
  Pressable,
} from "react-native";
import { router, useFocusEffect, usePathname } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { supabase } from "@/lib/supabase-client";
import useAuth from "@/hooks/useAuth";
import { ShiftType } from "@/types/db/shifts";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ModernCard, StatusBadge, TimeChip, ActionButton } from "@/components/crew";

interface GroupedShift {
  title: string;
  data: ShiftType[];
}

export default function AcceptedShiftsScreen() {
  const [shifts, setShifts] = useState<ShiftType[]>([]);
  const [expandedSections, setExpandedSections] = useState(new Set(["Today", "Tomorrow", "This Week", "Later"]));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const pathname = usePathname();
  const { user, loading: authLoading, displayNames } = useAuth();
  const { colors } = useTheme();
  const me = user?.id ?? "";

  const fetchShifts = useCallback(
    async (showLoader = true) => {
      // Don't fetch if auth is still loading or no valid user ID
      if (authLoading || !user?.id) {
        if (showLoader) setLoading(authLoading); // Keep loading state in sync with auth
        return;
      }

      if (showLoader) setLoading(true);
      try {
        const { data: rawData, error: fnError } = await supabase.functions.invoke("get-shifts", {
          body: { role: "crew" },
        });
        if (fnError) throw new Error(fnError.message || "Failed to fetch shifts");
        const rows: ShiftType[] = Array.isArray(rawData) ? rawData : [];
        // Filter only accepted shifts and sort by start time
        const acceptedShifts = rows
          .filter((shift) => {
            const positions = shift.shift_positions || [];
            return positions.some((pos) => pos.shift_users?.some((su) => su.id === me && su.status === "ACCEPTED"));
          })
          .sort((a, b) => {
            const aTime = a.start_time ? new Date(a.start_time).getTime() : 0;
            const bTime = b.start_time ? new Date(b.start_time).getTime() : 0;
            return aTime - bTime;
          });
        setShifts(acceptedShifts);
        setErr(null);
      } catch (e: any) {
        setErr(e.message ?? `Failed to load ${displayNames?.shifts?.plural?.toLowerCase() || "shifts"}`);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [me, authLoading, user?.id],
  );

  useFocusEffect(
    useCallback(() => {
      if (!authLoading && user?.id) {
        fetchShifts();
      }
    }, [fetchShifts, authLoading, user?.id]),
  );

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    fetchShifts(false);
  }, [fetchShifts]);

  const toggleSection = (title: string) => {
    setExpandedSections((expandedSections) => {
      const next = new Set(expandedSections);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });
  };

  // Group shifts by time period
  const groupedShifts = useMemo((): GroupedShift[] => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
    const weekEnd = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

    const groups: GroupedShift[] = [];
    const pastShifts: ShiftType[] = [];
    const todayShifts: ShiftType[] = [];
    const tomorrowShifts: ShiftType[] = [];
    const thisWeekShifts: ShiftType[] = [];
    const laterShifts: ShiftType[] = [];

    shifts.forEach((shift) => {
      if (!shift.start_time) {
        laterShifts.push(shift);
        return;
      }
      const startDate = new Date(shift.start_time);
      if (startDate < today) {
        pastShifts.push(shift);
      } else if (startDate < tomorrow) {
        todayShifts.push(shift);
      } else if (startDate < new Date(tomorrow.getTime() + 24 * 60 * 60 * 1000)) {
        tomorrowShifts.push(shift);
      } else if (startDate < weekEnd) {
        thisWeekShifts.push(shift);
      } else {
        laterShifts.push(shift);
      }
    });

    if (pastShifts.length > 0) groups.push({ title: "Previous", data: pastShifts });
    if (todayShifts.length > 0) groups.push({ title: "Today", data: todayShifts });
    if (tomorrowShifts.length > 0) groups.push({ title: "Tomorrow", data: tomorrowShifts });
    if (thisWeekShifts.length > 0) groups.push({ title: "This Week", data: thisWeekShifts });
    if (laterShifts.length > 0) groups.push({ title: "Later", data: laterShifts });

    return groups;
  }, [shifts]);

  const getShiftStatus = (shift: ShiftType) => {
    if (!shift.start_time || !shift.end_time) return "upcoming";
    const now = new Date();
    const start = new Date(shift.start_time);
    const end = new Date(shift.end_time);

    if (now < start) return "upcoming";
    if (now > end) return "completed";
    return "active";
  };

  // Get venue type (shift-level or project fallback), null if undefined/Unspecified
  const getVenueType = (shift: ShiftType): string | null => {
    const shiftVenue = (shift.meta_data as any)?.venue_type;
    if (shiftVenue && shiftVenue !== "Unspecified") return shiftVenue;
    const projectVenue = (shift.project as any)?.meta_data?.venue_type;
    if (projectVenue && projectVenue !== "Unspecified") return projectVenue;
    return null;
  };

  const ListFooter = () => (
    <View style={styles.listFooter}>
      <MaterialCommunityIcons name="check-circle-outline" size={24} color={colors.placeholder} />
      <ThemedText style={[styles.footerText, { color: colors.textSecondary }]}>You&apos;re all caught up</ThemedText>
    </View>
  );

  const renderShiftCard = ({ item: shift }: { item: ShiftType }) => {
    const status = getShiftStatus(shift);
    const userPosition = shift.shift_positions?.find((pos) => pos.shift_users?.some((su) => su.id === me));

    const startTime = shift.start_time ? new Date(shift.start_time) : null;
    const endTime = shift.end_time ? new Date(shift.end_time) : null;
    const duration =
      startTime && endTime ? Math.round(((endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60)) * 10) / 10 : 0;

    const payrollRate = userPosition?.payroll_rate?.rate || 0;
    const gifted = userPosition?.gifted_hours || 0;
    const minEarnings = gifted * payrollRate;
    const maxEarnings = duration * payrollRate;

    const getStatusVariant = () => {
      switch (status) {
        case "active":
          return "active";
        case "completed":
          return "completed";
        default:
          return "ready";
      }
    };

    const getStatusText = () => {
      switch (status) {
        case "active":
          return "IN PROGRESS";
        case "completed":
          return "COMPLETED";
        default:
          return "UPCOMING";
      }
    };

    const getStatusIcon = () => {
      switch (status) {
        case "active":
          return "clock-fast";
        case "completed":
          return "check-circle";
        default:
          return "clock-outline";
      }
    };

    const showClockInButton =
      status === "upcoming" && startTime && new Date() > new Date(startTime.getTime() - 15 * 60 * 1000);

    return (
      <View style={styles.timelineItem}>
        {/* Timeline connector */}
        <View style={[styles.timelineConnector, { backgroundColor: colors.border }]} />

        {/* Timeline dot */}
        <View
          style={[
            styles.timelineDot,
            { backgroundColor: status === "completed" ? colors.emerald?.border : colors.primary },
          ]}
        />

        {/* Card */}
        <TouchableOpacity
          onPress={() =>
            router.push({
              pathname: `${pathname.includes("/crew/") ? "crew/home" : "home"}/${shift.id}` as any,
              params: { context: "accepted" },
            })
          }
          activeOpacity={0.7}
          style={styles.cardTouchable}
        >
          <ModernCard style={styles.modernShiftCard}>
            <View style={styles.modernCardHeader}>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.modernProjectName} numberOfLines={1}>
                  {shift.name || `Untitled ${displayNames?.shifts?.name || "Shift"}`}
                  {"  "}
                  <ThemedText style={[styles.shiftId, { color: colors.placeholder }]}>
                    {shift.id?.slice(0, 6).toUpperCase()}
                  </ThemedText>
                </ThemedText>
                {(shift.project?.client?.name || shift.project?.name) && (
                  <ThemedText style={[styles.modernPositionName, { color: colors.textSecondary }]} numberOfLines={1}>
                    {[shift.project?.client?.name, shift.project?.name].filter(Boolean).join(" · ")}
                  </ThemedText>
                )}
                {userPosition?.position?.name && (
                  <ThemedText style={[styles.positionName, { color: colors.primary }]} numberOfLines={1}>
                    {userPosition.position.name}
                  </ThemedText>
                )}
                {(() => {
                  const loc = shift.location || shift.project?.location;
                  if (!loc?.name) return null;
                  const addr = [loc.address?.city, loc.address?.state].filter(Boolean).join(", ");
                  return (
                    <ThemedText style={[styles.modernPositionName, { color: colors.textSecondary }]} numberOfLines={1}>
                      {addr ? `${loc.name} · ${addr}` : loc.name}
                    </ThemedText>
                  );
                })()}
              </View>

              <StatusBadge variant={getStatusVariant()} text={getStatusText()} icon={getStatusIcon()} size="small" />
            </View>

            <View style={styles.modernCardDetails}>
              {/* Date chip */}
              {startTime && (
                <TimeChip
                  type="status"
                  text={startTime.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}
                  size="small"
                />
              )}

              {/* Time chip */}
              {startTime && (
                <TimeChip
                  type="time"
                  text={startTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  size="small"
                />
              )}

              {/* Duration chip */}
              {duration > 0 && <TimeChip type="duration" text={`~${duration}h`} size="small" />}

              {/* Rate chip */}
              {payrollRate > 0 && <TimeChip type="status" text={`$${payrollRate}/hr`} size="small" />}

              {/* Earnings chip */}
              {payrollRate > 0 && (
                <TimeChip
                  type="earnings"
                  text={
                    minEarnings === maxEarnings
                      ? `$${minEarnings.toFixed(0)}`
                      : `$${minEarnings.toFixed(0)} - $${maxEarnings.toFixed(0)}`
                  }
                  size="small"
                />
              )}

              {/* Venue chip */}
              {getVenueType(shift) && <TimeChip type="status" text={getVenueType(shift)!} size="small" />}
            </View>

            {showClockInButton && (
              <ActionButton
                title="Clock In"
                icon="clock-in"
                onPress={() =>
                  router.push({
                    pathname: `${pathname.includes("/crew/") ? "crew/home" : "home"}/${shift.id}` as any,
                    params: { context: "accepted" },
                  })
                }
                variant="success"
                size="small"
                style={styles.clockInButton}
              />
            )}
          </ModernCard>
        </TouchableOpacity>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.background }]}>
        <ActivityIndicator size="large" color={colors.primary} />
        <ThemedText style={{ marginTop: 12, opacity: 0.6 }}>
          Loading your {displayNames?.shifts?.plural?.toLowerCase() || "shifts"}...
        </ThemedText>
      </View>
    );
  }

  if (err) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.background }]}>
        <MaterialCommunityIcons name="alert-circle-outline" size={48} color={colors.red.text} />
        <ThemedText style={{ marginTop: 12, fontSize: 16 }}>{err}</ThemedText>
      </View>
    );
  }

  if (shifts.length === 0) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.background }]}>
        <MaterialCommunityIcons name="calendar-blank-outline" size={64} color={colors.placeholder} />
        <ThemedText style={styles.emptyTitle}>No Accepted {displayNames?.shifts?.plural || "Shifts"}</ThemedText>
        <ThemedText style={[styles.emptySubtitle, { color: colors.textSecondary }]}>
          Your accepted {displayNames?.shifts?.plural?.toLowerCase() || "shifts"} will appear here
        </ThemedText>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <SectionList
        sections={groupedShifts}
        keyExtractor={(item, index) => item.id ?? `shift-${index}`}
        extraData={expandedSections}
        renderItem={({ section: { title }, item }) => {
          if (!expandedSections.has(title)) return null;
          return renderShiftCard({ item });
        }}
        renderSectionHeader={({ section }) => (
          <View style={[styles.sectionHeader, { backgroundColor: colors.background }]}>
            <Pressable onPress={() => toggleSection(section.title)}>
              <ThemedText style={[styles.sectionTitle, { color: colors.text }]}>
                <TimeChip
                  type="duration"
                  style={{ minWidth: 50, justifyContent: "center" }}
                  text={section.data.length.toString()}
                  size="small"
                />{" "}
                {section.title}
                <MaterialCommunityIcons
                  name={expandedSections.has(section.title) ? "chevron-down" : "chevron-up"}
                  size={18}
                  color={colors.placeholder}
                />
              </ThemedText>
            </Pressable>
          </View>
        )}
        contentContainerStyle={styles.listContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />}
        showsVerticalScrollIndicator={false}
        stickySectionHeadersEnabled={false}
        ListFooterComponent={shifts.length > 0 ? ListFooter : null}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingTop: 40,
    flex: 1,
  },
  center: {
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },

  // Header
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingTop: 60,
    paddingBottom: 20,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: "800",
    color: "white",
  },
  headerSubtitle: {
    fontSize: 15,
    marginTop: 4,
    color: "white",
    opacity: 0.9,
  },
  headerBadgeContainer: {
    alignItems: "center",
  },
  headerBadge: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
  },
  headerBadgeText: {
    fontSize: 16,
    fontWeight: "700",
  },

  // List
  listContent: {
    paddingBottom: 32,
  },
  sectionHeader: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 8,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: "600",
    textTransform: "uppercase",
    opacity: 0.7,
  },

  // Timeline & Cards
  timelineItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingHorizontal: 16,
    marginBottom: 16,
    position: "relative",
  },
  timelineConnector: {
    position: "absolute",
    left: 27,
    top: 20,
    bottom: -16,
    width: 2,
    opacity: 0.3,
  },
  timelineDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginTop: 8,
    marginRight: 16,
    zIndex: 1,
  },
  cardTouchable: {
    flex: 1,
  },
  modernShiftCard: {
    margin: 0,
  },
  modernCardHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 12,
    gap: 8,
  },
  modernProjectName: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 2,
  },
  shiftId: {
    fontSize: 12,
    fontWeight: "400",
  },
  modernPositionName: {
    fontSize: 13,
  },
  positionName: {
    fontSize: 12,
    fontWeight: "500",
    marginTop: 2,
  },
  modernCardDetails: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 12,
  },
  clockInButton: {
    marginTop: 8,
  },

  // Empty state
  emptyTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginTop: 16,
  },
  emptySubtitle: {
    fontSize: 14,
    marginTop: 4,
  },
  listFooter: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 32,
    gap: 8,
  },
  footerText: {
    fontSize: 14,
  },
});
